'use client'

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'

import {
  Alert,
  AlertDescription,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { schoolGradeLabel } from '@helper/utils/domain'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { HomeConnectRequestDetail } from './request-summary'
import { useSharedTRPC } from '../../context'
import { OptionRow } from './option-row'

interface ConnectExistingStepProps {
  requestId: string
  detail: HomeConnectRequestDetail
  isSubmitting: boolean
  onConfirm: (childId: string) => void
  onCreateNew: () => void
  onBack: () => void
}

const longDate = (value: Date | string) =>
  new Date(value).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })

/**
 * "Hubungkan ke anak yang ada" — the merge path.
 *
 * A child who already belongs to a school cannot absorb a second one, so those rows are shown with
 * their school and disabled rather than hidden: a parent whose child is listed but unpickable needs
 * to understand why, and hiding the row reads as "my child is missing".
 */
export function ConnectExistingStep({
  requestId,
  detail,
  isSubmitting,
  onConfirm,
  onCreateNew,
  onBack,
}: ConnectExistingStepProps) {
  const trpc = useSharedTRPC()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string | null>(null)

  const { data, isPending } = useQuery(trpc.homeConnect.listCandidateChildren.queryOptions())
  const children = useMemo(() => data?.children ?? [], [data?.children])

  /**
   * What picking this child costs. Fetched per selection rather than up front: a merge is
   * irreversible and the seat rule can shorten premium the child already had, and the answer depends
   * entirely on which child is picked.
   */
  // `isPending`, not `isFetching`: a background refetch must update the notice in place rather than
  // blank it and grey out the button mid-read. The key carries `childId`, so switching child is a new
  // query and correctly shows the skeleton again instead of the previous child's numbers.
  const {
    data: impact,
    isPending: isCheckingImpact,
    isError: impactFailed,
  } = useQuery({
    ...trpc.homeConnect.previewMerge.queryOptions({ requestId, childId: selected ?? '' }),
    enabled: selected !== null,
  })

  // `blockers` are the guards the merge itself throws on. Any of them means this child cannot absorb
  // the student, so the button goes rather than the text showing.
  // A request the school has since cancelled is the same answer for the parent: `accept` will refuse.
  const isBlocked = (impact?.blockers.length ?? 0) > 0 || impact?.isActionable === false

  const coverageEndsAt = impact?.seats.coverageEndsAt ?? null
  // Non-null only when going ahead ends the child's premium earlier than keeping the school's seat
  // would have, so its presence IS the "you are giving something up" signal.
  const shortenedFrom = impact?.seats.coverageWithSchoolSeatEndsAt ?? null
  const releasedSeatCount = impact?.seats.releasedSchoolSeatCount ?? 0
  // Non-null means the merge leaves the child with nothing covering today — a gap no end date shows.
  const resumesAt = impact?.seats.coverageResumesAt ?? null
  const selectedName = children.find((child) => child.id === selected)?.name

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return children
    return children.filter((child) => child.name.toLowerCase().includes(term))
  }, [children, search])

  return (
    <>
      <DialogHeader>
        <DialogTitle>Hubungkan ke anak yang ada</DialogTitle>
        <DialogDescription>Anak kamu yang mana bernama {detail.studentName}?</DialogDescription>
      </DialogHeader>

      <div className='srd:flex srd:items-center srd:gap-x-3'>
        <Avatar profile='user'>
          {detail.studentImage && <AvatarImage src={detail.studentImage} alt={detail.studentName} />}
          <AvatarFallback>{getInitials(detail.studentName)}</AvatarFallback>
        </Avatar>
        <span className='srd:flex srd:flex-col'>
          <span className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>
            {detail.studentName}
          </span>
          {detail.studentGrade && (
            <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
              {schoolGradeLabel(detail.studentGrade)}
            </span>
          )}
        </span>
      </div>

      <div className='srd:relative'>
        <Search className='srd:text-grey-100 srd:absolute srd:left-3 srd:top-1/2 srd:size-4 srd:-translate-y-1/2' />
        <Input
          value={search}
          placeholder='Cari'
          aria-label='Cari anak'
          className='srd:pl-9'
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div
        role='radiogroup'
        aria-label='Pilih anak'
        className='srd:flex srd:max-h-[280px] srd:flex-col srd:gap-y-1 srd:overflow-y-auto'
      >
        {isPending && <Skeleton className='srd:h-16 srd:w-full' />}

        {!isPending && visible.length === 0 && (
          <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100 srd:px-4 srd:py-6')}>
            {children.length === 0 ? 'Kamu belum punya anak terdaftar.' : 'Tidak ada anak yang cocok.'}
          </p>
        )}

        {visible.map((child) => (
          <OptionRow
            key={child.id}
            markerSide='end'
            selected={selected === child.id}
            disabled={child.schoolName !== null}
            onSelect={() => setSelected(child.id)}
          >
            <Avatar profile='user'>
              {child.image && <AvatarImage src={child.image} alt={child.name} />}
              <AvatarFallback>{getInitials(child.name)}</AvatarFallback>
            </Avatar>
            <span className='srd:flex srd:min-w-0 srd:flex-col'>
              <span className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black srd:truncate')}>
                {child.name}
              </span>
              <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100 srd:truncate')}>
                {child.schoolName ?? 'Tidak ada sambungan sekolah'}
              </span>
            </span>
          </OptionRow>
        ))}
      </div>

      {selected && isCheckingImpact && <Skeleton className='srd:h-16 srd:w-full' />}

      {/* Never let a failed check read as permission: the confirm button stays disabled below whenever
          `impact` is missing, so this is the only thing standing between the parent and an
          irreversible merge they were never shown the cost of. */}
      {selected && !isCheckingImpact && impactFailed && (
        <Alert variant='error'>
          <AlertDescription>
            Gagal memeriksa akibat penggabungan. Periksa koneksi kamu, lalu pilih ulang anaknya.
          </AlertDescription>
        </Alert>
      )}

      {selected && !isCheckingImpact && !impactFailed && isBlocked && (
        <Alert variant='error'>
          <AlertDescription>
            {impact?.isActionable === false
              ? 'Permohonan ini sudah tidak bisa dijawab. Tutup dialog ini dan periksa daftar pesanmu.'
              : `${selectedName ?? 'Anak ini'} tidak bisa digabungkan dengan ${detail.studentName}. Pilih anak lain, atau buat akun anak baru.`}
          </AlertDescription>
        </Alert>
      )}

      {selected && !isCheckingImpact && !impactFailed && !isBlocked && impact && (
        <Alert variant={shortenedFrom || resumesAt ? 'error' : 'neutral'}>
          <AlertDescription className='srd:flex srd:flex-col srd:gap-y-1'>
            {/* The seat rule keeps the family's seat and hands the school's back, so the school seat
                being the longer of the two is a real loss the parent has to see before agreeing. */}
            {shortenedFrom && coverageEndsAt && (
              <span>
                {selectedName ?? 'Anak ini'} sudah punya kursi premium keluarga sampai {longDate(coverageEndsAt)}. Kursi
                dari {detail.schoolName} yang berlaku sampai {longDate(shortenedFrom)} akan dikembalikan ke sekolah,
                jadi akses premiumnya berakhir lebih awal.
              </span>
            )}
            {!shortenedFrom && coverageEndsAt && (
              <span>
                Akses premium {selectedName ?? 'anak ini'} berlaku sampai {longDate(coverageEndsAt)}.
              </span>
            )}
            {!coverageEndsAt && <span>{selectedName ?? 'Anak ini'} belum punya akses premium.</span>}
            {resumesAt && (
              <span>
                Akses premiumnya terputus mulai hari ini dan baru aktif lagi {longDate(resumesAt)}, karena kursi
                keluarganya belum berlaku.
              </span>
            )}
            {releasedSeatCount > 0 && (
              <span>
                {releasedSeatCount} kursi premium dikembalikan ke {detail.schoolName}.
              </span>
            )}
            <span>Penggabungan tidak bisa dibatalkan.</span>
          </AlertDescription>
        </Alert>
      )}

      <DialogFooter className='srd:flex-col srd:gap-y-2'>
        <Button
          className='srd:w-full'
          loading={isSubmitting}
          // `impactFailed` and `!impact` are the invariant, not duplicates of the terms before them:
          // the merge is irreversible, so no state where the cost is unknown or stale may enable this.
          // A refetch that fails AFTER a success keeps `data`, so `!impact` alone would let the button
          // live while the only thing on screen is the failure alert.
          disabled={!selected || isSubmitting || isCheckingImpact || isBlocked || impactFailed || !impact}
          onClick={() => selected && onConfirm(selected)}
        >
          Konfirmasi dan terima
        </Button>
        <Button variant='outline' className='srd:w-full' disabled={isSubmitting} onClick={onCreateNew}>
          Buat akun anak baru saja
        </Button>
        <Button variant='ghost' className='srd:w-full' disabled={isSubmitting} onClick={onBack}>
          Kembali ke permohonan
        </Button>
      </DialogFooter>
    </>
  )
}
